import { useContext, useEffect, useRef, useState } from "react"
import { TEmailAPIRequestLogs } from "./types"
import { EmailAPIContext } from "../context"
import Box from "@mui/material/Box"
import { Button, Chip, DataTable, EEmailLogType, EEMailSummaryStatuses, EmailAPIFactory, EmptyContent, GenericWrapperContext, getCalendarFormat, getDate, getStartOfDate, LoadingContainer, TColors, TDataTableColumnsData, TDataTableRowData, TEmailActivitySummary, TGenericListReturn, TPromisePoolRef, TPromisePoolReturnData } from "@cakemail-org/ui-components-v2"
import { useTranslation } from "react-i18next"
import classNames from "classnames"
import "./styles.scss"
import { formatDelta, getAPIDateRange } from "../../../utils/date"
import { returnEmailTagsQuery, returnProviderQuery } from "../../../utils/data"
import { NameTableCol } from "../../../components/tables/nameTableCol"

export function EmailAPIRequestLogs({ }: TEmailAPIRequestLogs) {
    const genericWrapper = useContext(GenericWrapperContext)
    const { timeRange, tagsFilter, search, setRightHandMenu, rightHandMenu, providerFilter, filterOperator, refreshKey, status} = useContext(EmailAPIContext)
    const { t, i18n } = useTranslation()
    const [metrics, setMetrics] = useState<(TGenericListReturn<TEmailActivitySummary>)>()

    const [page, setPage] = useState<number>(1)

    const [loading, setLoading] = useState<boolean>(true)

    const [error, setError] = useState<boolean>(false)

    useEffect(() => {
        setPage(1)
    }, [search, timeRange, tagsFilter])

    useEffect(() => {
        fetchMetrics()
    }, [timeRange, tagsFilter, page, search, refreshKey,status])

    useEffect(() => {

        return () => {
            setRightHandMenu()
        }
    }, [])


    function fetchMetrics() {
        setLoading(true)
        const apiTimeRange = getAPIDateRange({ range: timeRange })
        EmailAPIFactory.getEmailActivitySummary({
            tags: returnEmailTagsQuery(tagsFilter, { operator: filterOperator }),
            providers: returnProviderQuery({ providers: providerFilter, operator: filterOperator }),
            email: search ? "~~" + search : undefined,
            page,
            status: status ? status : undefined,
            perPage: 100,
            startTime: apiTimeRange.start,
            endTime: apiTimeRange.end,
            withCount: true
        }).then((data) => {
            setError(false)
            setMetrics(data)
            setLoading(false)
        }).catch(() => {
            setError(true)
        })
    }

    function handlePaginationChange(cPage: number, cPerPage: number) {
        setPage(cPage)
    }

    function handleRowClick(event: React.MouseEvent<unknown>, row: TDataTableRowData) {
        setRightHandMenu({ type: "logInspect", id: row.email_id })
    }

    function getStatusColor(status: EEmailLogType): TColors {
        switch (status) {
            case EEmailLogType.submitted:
            case EEmailLogType.queued:
            case EEmailLogType.processing:
            case EEmailLogType.delivered:
                return "secondary"
            case EEmailLogType.click:
            case EEmailLogType.open:
            case EEmailLogType.other:
                return "info"
            case EEmailLogType.unsubscribe:
            case EEmailLogType.global_unsubscribe:
            case EEmailLogType.global_unsubscribe_reason:
                return "warning"
            case EEmailLogType.bounce:
            case EEmailLogType.spam:
            case EEmailLogType.error:
            case EEmailLogType.rejected:
                return "error"
            default:
                return "secondary"
        }
    }

    const colsData: TDataTableColumnsData[] = [
        {
            id: "status",
            label: t("general-status"),
        },
        {
            id: "email",
            label: t("general-message")
        },
        {
            id: "submitted_time",
            label: t("general-submitted_time"),
        },
        {
            id: "last_engagement_time",
            label: t("general-last-event-received"),
        },
        {
            id: "opens",
            label: t("general-open_other"),
        },
        {
            id: "clicks",
            label: t("general-clicks"),
        },
        {
            id: "timeToDeliver",
            label: t("general-time-to-deliver"),
        },
        {
            id: "timeToClick",
            label: t("general-time-to-click"),
        },
        {
            id: "actions",
            label: ""
        }
    ]

    const rows: TDataTableRowData[] = metrics?.data?.map((activityRow) => {
        return {
            email_id: activityRow.email_id,
            isSelected: activityRow.email_id === rightHandMenu?.id,
            status: <Chip label={t(`general-${activityRow.status}`)} color={getStatusColor(activityRow.status)} className="statusChip" squared tinted />,
            email: <NameTableCol ellipsis name={`${t("general-to")}: ${activityRow.email}`} desc={activityRow.subject} />,
            submitted_time: activityRow.submitted_time ? getCalendarFormat({ unixDate: activityRow.submitted_time }) : "",
            last_engagement_time: activityRow.last_engagement_time ? getCalendarFormat({ unixDate: activityRow.last_engagement_time }) : "",
            opens: activityRow.open || "",
            clicks: activityRow.click || "",
            timeToDeliver: (activityRow.delivered_time && activityRow.submitted_time) ? formatDelta(activityRow.delivered_time, activityRow.submitted_time) : "",
            timeToClick: (activityRow.first_click_time && activityRow.delivered_time) ? formatDelta(activityRow.first_click_time, activityRow.delivered_time) : "",
            actions: <Button onClick={() => {
                setRightHandMenu({ type: "logInspect", id: activityRow.email_id })
            }} size="small" color="body1" variant="outlined">{t("general-view-details")}</Button>
        }
    }) || []

    const classnames = classNames("emailAPIRequestLogs", {
        "loading": loading
    })

    return <Box className={classnames} >
        <LoadingContainer loading={!metrics && !error}>
            {metrics && <>
                <DataTable
                    columnsData={colsData}
                    rows={rows}
                    onPaginationChange={handlePaginationChange}
                    totalCount={metrics.pagination.count}
                    showPagination
                    hasMore={metrics.pagination.has_more}
                    initState={{ page }}
                    onRowClick={handleRowClick}
                />
            </>}
            {error && <EmptyContent
                title={t("analytic-block-loading-error-title")}
            />}
        </LoadingContainer>
    </Box>
}